import React, { useMemo, useRef, useEffect, useCallback } from 'react'
import { useVolatileReducer } from 'helpers'
import top5Reducer from './store/Reducer'
import Top5Service from './Service'
import { top5InitialState } from './store/State'
import {
  IWordBank,
  IResponseUpdateRequestPayload,
  ICreateOwnWordBankPayload,
  WordBankCategory,
  IResponsesValue
} from 'types'
import UserResponsesManagerService from 'smart-components/user-responses-manager/Service'
import { useServiceState, useNotification } from 'helpers'
import isActiveUser from 'helpers/is-active-user'
import SessionManagerService from 'smart-components/session-manager/Service'
import { IProps } from './Interface'

const top5Context = {
  state: {
    ...top5InitialState,
    userWordbanks: {} as { [key in WordBankCategory]: IWordBank[] },
    isResponsesLoaded: false
  },
  handlers: {
    canAddNewTag: (_category: WordBankCategory) => false,
    getWordBanks: (_userId: string) => new Promise<void>(() => {}),
    handleTagClick: (_wordbank: IWordBank, _errorMessage?: string) => new Promise<void>(() => {}),
    handleAddOwnClick: (_payload: ICreateOwnWordBankPayload) => new Promise<void>(() => {})
  }
}

export type Top5Context = typeof top5Context

export const Top5ContextProvider = React.createContext<Top5Context>(top5Context)

const Provider: React.FC<IProps> = ({ children, responses, user }) => {
  const [state, dispatch] = useVolatileReducer(top5Reducer, top5InitialState)
  const userResponsesManageState = useServiceState(UserResponsesManagerService, false)
  const { emitError, emitWarning } = useNotification()
  const mountRef = useRef(false)
  const currentUser = user || SessionManagerService.state.currentUser

  const computedState = useMemo(() => {
    const currentResponses = responses || userResponsesManageState.userResponses?.responses || ({} as IResponsesValue)

    return {
      ...state,
      userWordbanks: {
        [WordBankCategory.CompanyDescriptor]: currentResponses.thriveEnv || [],
        [WordBankCategory.Xfactor]: currentResponses.teamMembersSay || [],
        [WordBankCategory.TalentTags]: currentResponses.selfTag || []
      },
      isResponsesLoaded: Boolean(Object.keys(currentResponses).length)
    }
  }, [state, userResponsesManageState, responses])

  const updateResponse = useCallback(
    async (responseUpdate: IResponseUpdateRequestPayload) => {
      const error = await UserResponsesManagerService.updateResponse(responseUpdate)

      if (error) {
        emitError(error)
      }
    },
    [emitError]
  )

  const handlers = useMemo(() => {
    return {
      handleTagClick: Top5Service.onTagClick(computedState, dispatch, updateResponse, emitError),
      handleAddOwnClick: Top5Service.onAddOwnClick(dispatch, computedState, updateResponse, emitError),
      getWordBanks: Top5Service.onMount(dispatch, emitError),
      canAddNewTag: Top5Service.canAddNewTag(computedState, emitError)
    }
  }, [computedState, emitError, updateResponse, dispatch])

  const contextValue = useMemo(
    () => ({
      ...top5Context,
      state: computedState,
      handlers
    }),
    [computedState, handlers]
  )

  useEffect(() => {
    if (
      !mountRef.current &&
      computedState.isResponsesLoaded &&
      !computedState.userWordbanks.talentTags.length &&
      !computedState.userWordbanks.companyDescriptor &&
      !computedState.userWordbanks.xfactor.length
    ) {
      emitWarning('Hover and click words that describe you!')
    }

    if (
      isActiveUser(currentUser) &&
      !mountRef.current &&
      !state.isLoadingCategories &&
      !(state.wordBanks.companyDescriptor || state.wordBanks.talentTags || state.wordBanks.xfactor)
    ) {
      handlers.getWordBanks(currentUser._id)
    }

    mountRef.current = true
    return () => {
      mountRef.current = false
    }
  }, [])

  return useMemo(() => <Top5ContextProvider.Provider value={contextValue}>{children}</Top5ContextProvider.Provider>, [
    children,
    contextValue
  ])
}

export default Provider
